import React, { useState } from "react";
import { DateUtils } from "@cleanpro/common-ui";
import { useAuth } from "../../authentication/hooks/useAuth";
import { useRoleAccess } from "../hooks/useRoleAccess";
import {
  User,
  Mail,
  Phone,
  Calendar,
  Save,
  Camera,
  Globe,
  Bell,
  Palette,
  Shield,
  CheckCircle,
  XCircle,
  AlertCircle,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";

export default function Profile() {
  const [showEnrollment, setShowEnrollment] = useState(false);

  const { user, updateProfile, refreshProfile } = useAuth();
  const permissions = useRoleAccess();
  const [isEditing, setIsEditing] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Get active tab from URL or default to 'profile'
  const activeTab = searchParams.get("tab") || "profile";

  const handleTabChange = (tab: string) => {
    setSearchParams({ tab });
  };

  const [formData, setFormData] = useState({
    display_name: user?.profile?.display_name || user?.name || "",
    first_name: user?.profile?.first_name || "",
    last_name: user?.profile?.last_name || "",
    phone: user?.profile?.phone || "",
    bio: user?.profile?.bio || "",
    timezone: user?.profile?.timezone || "UTC",
    language: user?.profile?.language || "en",
    theme: user?.profile?.theme || "light",
  });
  const [notificationSettings, setNotificationSettings] = useState(() => {
    const settings = user?.profile?.notification_settings ?? {};
    return {
      email: typeof settings.email === "boolean" ? settings.email : true,
      sms: typeof settings.sms === "boolean" ? settings.sms : false,
      push: typeof settings.push === "boolean" ? settings.push : true,
      marketing:
        typeof settings.marketing === "boolean" ? settings.marketing : false,
    };
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | { type: 'success' | 'error'; text: string } | null>(null);

  // Check if user can edit profile
  if (!permissions.canEditProfile) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-gray-900">Access Denied</h1>
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 text-center">
          <p className="text-gray-500">
            You don't have permission to access profile settings.
          </p>
        </div>
      </div>
    );
  }
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    try {
      const profileData = {
        ...formData,
        notification_settings: notificationSettings,
      };
      await updateProfile(profileData);
      await refreshProfile();
      setMessage({ type: "success", text: "Profile updated successfully!" });
      setIsEditing(false);
    } catch {
      setMessage({ type: "error", text: "Failed to update profile. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      display_name: user?.profile?.display_name || user?.name || "",
      first_name: user?.profile?.first_name || "",
      last_name: user?.profile?.last_name || "",
      phone: user?.profile?.phone || "",
      bio: user?.profile?.bio || "",
      timezone: user?.profile?.timezone || "UTC",
      language: user?.profile?.language || "en",
      theme: user?.profile?.theme || "light",
    });
    setIsEditing(false);
    setMessage(null);
  };

  const handleEnrollmentSuccess = () => {
    setShowEnrollment(false);
    // checkMFAStatus();
    setMessage({
      type: "success",
      text: "Two-factor authentication has been enabled successfully!",
    });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleUnenroll = async () => {
    const verifiedFactor = mfaState.factors.find(
      (f) => f.status === "verified"
    );
    if (!verifiedFactor) return;

    setUnenrolling(true);
    const success = await unenrollMFA(verifiedFactor.id);
    setUnenrolling(false);
    setShowUnenrollConfirm(false);

    if (success) {
      setMessage({
        type: "success",
        text: "Two-factor authentication has been disabled.",
      });
    } else {
      setMessage({
        type: "error",
        text: mfaState.error || "Failed to disable two-factor authentication.",
      });
    }
    setTimeout(() => setMessage(null), 5000);
  };

  if (showEnrollment) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <MFAEnrollment
          onSuccess={handleEnrollmentSuccess}
          onCancel={() => setShowEnrollment(false)}
          required={isRequired}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Profile Settings</h1>
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Edit Profile
          </button>
        )}
      </div>

      {message && (
        <div
          className={`p-4 rounded-lg ${
            typeof message === 'string'
              ? message.includes("successfully")
                ? "bg-green-50 border border-green-200 text-green-800"
                : "bg-red-50 border border-red-200 text-red-800"
              : message.type === "success"
              ? "bg-green-50 border border-green-200 text-green-800"
              : "bg-red-50 border border-red-200 text-red-800"
          }`}
        >
          {typeof message === 'string' ? message : message.text}
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
        <div className="p-6">
          <div className="flex items-center space-x-6 mb-8">
            <div className="relative">
              <div className="h-24 w-24 bg-blue-100 rounded-full flex items-center justify-center">
                {user?.profile?.avatar_url ? (
                  <img
                    src={user.profile.avatar_url}
                    alt="Profile"
                    className="h-24 w-24 rounded-full object-cover"
                  />
                ) : (
                  <span className="text-2xl font-medium text-blue-700">
                    {(user?.profile?.display_name || user?.name || "U").charAt(
                      0
                    )}
                  </span>
                )}
              </div>
              {isEditing && (
                <button className="absolute bottom-0 right-0 bg-indigo-600 text-white p-2 rounded-full hover:bg-blue-700 transition-colors">
                  <Camera className="h-4 w-4" />
                </button>
              )}
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {user?.profile?.display_name || user?.name}
              </h2>
              <p className="text-gray-600 capitalize">{user?.role}</p>
              <p className="text-sm text-gray-500">{user?.email}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex">
            <button
              onClick={() => handleTabChange("profile")}
              className={`py-4 px-6 text-sm font-medium border-b-2 ${
                activeTab === "profile"
                  ? "border-blue-500 text-indigo-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Profile Information
            </button>
            <button
              onClick={() => handleTabChange("preferences")}
              className={`py-4 px-6 text-sm font-medium border-b-2 ${
                activeTab === "preferences"
                  ? "border-blue-500 text-indigo-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Preferences
            </button>
            <button
              onClick={() => handleTabChange("notifications")}
              className={`py-4 px-6 text-sm font-medium border-b-2 ${
                activeTab === "notifications"
                  ? "border-blue-500 text-indigo-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Notifications
            </button>
            <button
              onClick={() => handleTabChange("security")}
              className={`py-4 px-6 text-sm font-medium border-b-2 ${
                activeTab === "security"
                  ? "border-blue-500 text-indigo-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Security
            </button>
          </nav>
        </div>

        <div className="p-6">
          {activeTab === "profile" && (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <User className="h-4 w-4 inline mr-2" />
                    Display Name
                  </label>
                  <input
                    type="text"
                    required
                    disabled={!isEditing}
                    className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                      !isEditing
                        ? "bg-gray-50 text-gray-600"
                        : "border-gray-300"
                    }`}
                    value={formData.display_name}
                    onChange={(e) =>
                      setFormData({ ...formData, display_name: e.target.value })
                    }
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <User className="h-4 w-4 inline mr-2" />
                    First Name
                  </label>
                  <input
                    type="text"
                    disabled={!isEditing}
                    className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                      !isEditing
                        ? "bg-gray-50 text-gray-600"
                        : "border-gray-300"
                    }`}
                    value={formData.first_name}
                    onChange={(e) =>
                      setFormData({ ...formData, first_name: e.target.value })
                    }
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <User className="h-4 w-4 inline mr-2" />
                    Last Name
                  </label>
                  <input
                    type="text"
                    disabled={!isEditing}
                    className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                      !isEditing
                        ? "bg-gray-50 text-gray-600"
                        : "border-gray-300"
                    }`}
                    value={formData.last_name}
                    onChange={(e) =>
                      setFormData({ ...formData, last_name: e.target.value })
                    }
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Phone className="h-4 w-4 inline mr-2" />
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    disabled={!isEditing}
                    className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                      !isEditing
                        ? "bg-gray-50 text-gray-600"
                        : "border-gray-300"
                    }`}
                    value={formData.phone}
                    onChange={(e) =>
                      setFormData({ ...formData, phone: e.target.value })
                    }
                    placeholder="(555) 123-4567"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Mail className="h-4 w-4 inline mr-2" />
                    Email Address
                  </label>
                  <input
                    type="email"
                    disabled
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-50 text-gray-600"
                    value={user?.email || ""}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Email cannot be changed
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bio
                </label>
                <textarea
                  disabled={!isEditing}
                  rows={3}
                  className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                    !isEditing ? "bg-gray-50 text-gray-600" : "border-gray-300"
                  }`}
                  value={formData.bio}
                  onChange={(e) =>
                    setFormData({ ...formData, bio: e.target.value })
                  }
                  placeholder="Tell us about yourself..."
                />
              </div>

              {isEditing && (
                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 disabled:opacity-50"
                  >
                    <Save className="h-4 w-4" />
                    <span>{loading ? "Saving..." : "Save Changes"}</span>
                  </button>
                </div>
              )}
            </form>
          )}

          {activeTab === "preferences" && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Globe className="h-4 w-4 inline mr-2" />
                    Language
                  </label>
                  <select
                    disabled={!isEditing}
                    className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                      !isEditing
                        ? "bg-gray-50 text-gray-600"
                        : "border-gray-300"
                    }`}
                    value={formData.language}
                    onChange={(e) =>
                      setFormData({ ...formData, language: e.target.value })
                    }
                  >
                    <option value="en">English</option>
                    <option value="es">Spanish</option>
                    <option value="fr">French</option>
                    <option value="de">German</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Calendar className="h-4 w-4 inline mr-2" />
                    Timezone
                  </label>
                  <select
                    disabled={!isEditing}
                    className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                      !isEditing
                        ? "bg-gray-50 text-gray-600"
                        : "border-gray-300"
                    }`}
                    value={formData.timezone}
                    onChange={(e) =>
                      setFormData({ ...formData, timezone: e.target.value })
                    }
                  >
                    <option value="UTC">UTC</option>
                    <option value="America/New_York">Eastern Time</option>
                    <option value="America/Chicago">Central Time</option>
                    <option value="America/Denver">Mountain Time</option>
                    <option value="America/Los_Angeles">Pacific Time</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Palette className="h-4 w-4 inline mr-2" />
                    Theme
                  </label>
                  <select
                    disabled={!isEditing}
                    className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                      !isEditing
                        ? "bg-gray-50 text-gray-600"
                        : "border-gray-300"
                    }`}
                    value={formData.theme}
                    onChange={(e) =>
                      setFormData({ ...formData, theme: e.target.value })
                    }
                  >
                    <option value="light">Light</option>
                    <option value="dark">Dark</option>
                    <option value="auto">Auto</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {activeTab === "notifications" && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  <Bell className="h-5 w-5 inline mr-2" />
                  Notification Preferences
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        Email Notifications
                      </p>
                      <p className="text-sm text-gray-500">
                        Receive notifications via email
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={notificationSettings.email}
                        onChange={(e) =>
                          setNotificationSettings({
                            ...notificationSettings,
                            email: e.target.checked,
                          })
                        }
                        disabled={!isEditing}
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        SMS Notifications
                      </p>
                      <p className="text-sm text-gray-500">
                        Receive notifications via SMS
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={notificationSettings.sms}
                        onChange={(e) =>
                          setNotificationSettings({
                            ...notificationSettings,
                            sms: e.target.checked,
                          })
                        }
                        disabled={!isEditing}
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        Marketing Communications
                      </p>
                      <p className="text-sm text-gray-500">
                        Receive promotional emails and updates
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={notificationSettings.marketing}
                        onChange={(e) =>
                          setNotificationSettings({
                            ...notificationSettings,
                            marketing: e.target.checked,
                          })
                        }
                        disabled={!isEditing}
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "security" && (
            <div className="space-y-6">
              <div>
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div
                      className={`h-12 w-12 rounded-full flex items-center justify-center ${
                        mfaState.isEnrolled ? "bg-green-100" : "bg-gray-100"
                      }`}
                    >
                      <Shield
                        className={`h-6 w-6 ${
                          mfaState.isEnrolled
                            ? "text-green-600"
                            : "text-gray-400"
                        }`}
                      />
                    </div>
                  </div>

                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Two-Factor Authentication
                    </h3>

                    {message && (
                      <div
                        className={`mb-4 p-3 rounded-md flex items-start space-x-2 ${
                          typeof message === 'object' && message.type === "success"
                            ? "bg-green-50 border border-green-200"
                            : "bg-red-50 border border-red-200"
                        }`}
                      >
                        {typeof message === 'object' && message.type === "success" ? (
                          <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                        ) : (
                          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                        )}
                        <span
                          className={`text-sm ${
                            typeof message === 'object' && message.type === "success"
                              ? "text-green-700"
                              : "text-red-700"
                          }`}
                        >
                          {typeof message === 'string' ? message : message.text}
                        </span>
                      </div>
                    )}

                    <div className="space-y-4">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-600">Status:</span>
                        {mfaState.isEnrolled ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Enabled
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            <XCircle className="h-3 w-3 mr-1" />
                            Disabled
                          </span>
                        )}
                        {isRequired && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            Required
                          </span>
                        )}
                      </div>

                      <p className="text-sm text-gray-600">
                        {mfaState.isEnrolled
                          ? "Your account is protected with two-factor authentication. You'll need to enter a code from your authenticator app when you sign in."
                          : "Add an extra layer of security to your account by enabling two-factor authentication. You'll need an authenticator app to get started."}
                      </p>

                      {isRequired && !mfaState.isEnrolled && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 flex items-start space-x-2">
                          <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                          <div className="text-sm text-yellow-700">
                            <p className="font-medium">
                              Two-factor authentication is required for admin
                              users.
                            </p>
                            <p className="mt-1">
                              You must enable 2FA to continue using your
                              account.
                            </p>
                          </div>
                        </div>
                      )}

                      <div className="pt-2">
                        {mfaState.isEnrolled ? (
                          <>
                            {!showUnenrollConfirm ? (
                              <button
                                onClick={() => setShowUnenrollConfirm(true)}
                                disabled={isRequired}
                                className="px-4 py-2 border border-red-300 text-red-700 rounded-md text-sm font-medium hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                title={
                                  isRequired
                                    ? "Two-factor authentication is required for admin users"
                                    : "Disable two-factor authentication"
                                }
                              >
                                Disable 2FA
                              </button>
                            ) : (
                              <div className="bg-red-50 border border-red-200 rounded-md p-4 space-y-3">
                                <div className="flex items-start space-x-2">
                                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                                  <div className="text-sm text-red-700">
                                    <p className="font-medium">
                                      Are you sure you want to disable
                                      two-factor authentication?
                                    </p>
                                    <p className="mt-1">
                                      This will make your account less secure.
                                    </p>
                                  </div>
                                </div>
                                <div className="flex space-x-3">
                                  <button
                                    onClick={() =>
                                      setShowUnenrollConfirm(false)
                                    }
                                    className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded text-sm font-medium hover:bg-gray-50 transition-colors"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    onClick={handleUnenroll}
                                    disabled={unenrolling}
                                    className="px-3 py-1.5 border border-transparent bg-red-600 text-white rounded text-sm font-medium hover:bg-red-700 disabled:opacity-50 transition-colors"
                                  >
                                    {unenrolling
                                      ? "Disabling..."
                                      : "Yes, Disable"}
                                  </button>
                                </div>
                              </div>
                            )}
                          </>
                        ) : (
                          <button
                            onClick={() => setShowEnrollment(true)}
                            className="px-4 py-2 border border-transparent bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                          >
                            Enable 2FA
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                {/* <MFASettings /> */}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Account Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
            <div>
              <p className="text-gray-500">Role</p>
              <p className="font-medium text-gray-900 capitalize">
                {user?.role}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Member Since</p>
              <p className="font-medium text-gray-900">January 2024</p>
            </div>
            <div>
              <p className="text-gray-500">Last Login</p>
              <p className="font-medium text-gray-900">
                Today at {DateUtils.formatTimeFromDate(new Date())}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Status</p>
              <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                Active
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
